import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Dropdown, Tab, Tabs } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import PropTypes from 'prop-types';
import { fetchNotificationList, notificationMarkAsUnread, fetchNotificationUnreadCount } from "../../../redux/Slices/common/CommonSlice";
import ProcessingFollowUpPopupView from "../popups/processing-follow-up/ProcessingFollowUpPopupView";
import ViewUpdateTaskCalendar from "../popups/calendar/ViewUpdateTaskCalendar";
import translation from "../../../utils/translation";
import NotFoundMessage from "../../ReusableComponents/NotFoundMessage";
import PaymentFollowUpPopupView from "../popups/payment-follow-up/PaymentFollowUpPopupView";
import notificationBlue from "../../../assets/img/svg/notification.svg";
import crossCircleIcon from "../../../assets/img/svg/cross-circle-line.svg";
import "./notification-dropdown.css";

const NotificationDropdown = ({ notificationUnreadCount }) => {
    const dispatch = useDispatch();
    const [isProcessingFollowUpView, setIsProcessingFollowUpView] = useState(false);
    const [isPaymentFollowUpView, setIsPaymentFollowUpView] = useState(false);
    const [isTaskViewPopup, setIsTaskViewPopup] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState({});
    // const { notificationList } = useSelector((state) => state.common);
    const [key, setKey] = useState('all');
    const dropdownToggleRef = useRef(null);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [allNotifications, setAllNotifications] = useState([]);
    const listRef = useRef(null);

    // Unread tab states
    const [unreadPage, setUnreadPage] = useState(1);
    const [unreadLoading, setUnreadLoading] = useState(false);
    const [unreadHasMore, setUnreadHasMore] = useState(true);
    const [unreadNotificationsList, setUnreadNotificationsList] = useState([]);
    const unreadListRef = useRef(null);

    useEffect(() => {
        setAllNotifications([]);
        setPage(1);
        setHasMore(true);
        setUnreadNotificationsList([]);
        setUnreadPage(1);
        setUnreadHasMore(true);
    }, [key]);

    useEffect(() => {
        if (key === 'all') {
            setLoading(true);
            dispatch(fetchNotificationList({ limit: 10, page })).then((res) => {
                const newNotifications = res?.payload?.data || [];
                setAllNotifications((prev) => page === 1 ? newNotifications : [...prev, ...newNotifications]);
                setHasMore(newNotifications.length === 10);
                setLoading(false);
            });
        } else if (key === 'unread') {
            setUnreadLoading(true);
            dispatch(fetchNotificationList({ limit: 10, page: unreadPage, is_read: false })).then((res) => {
                const newUnread = res?.payload?.data || [];
                setUnreadNotificationsList((prev) => unreadPage === 1 ? newUnread : [...prev, ...newUnread]);
                setUnreadHasMore(newUnread.length === 10);
                setUnreadLoading(false);
            });
        }
    }, [dispatch, page, key, unreadPage]);

    // Infinite scroll handler
    const handleScroll = useCallback(() => {
        if (!listRef.current || loading || !hasMore) return;
        const { scrollTop, scrollHeight, clientHeight } = listRef.current;
        if (scrollHeight - scrollTop - clientHeight < 50) {
            setPage((prev) => prev + 1);
        }
    }, [loading, hasMore]);

    const handleUnreadScroll = useCallback(() => {
        if (!unreadListRef.current || unreadLoading || !unreadHasMore) return;
        const { scrollTop, scrollHeight, clientHeight } = unreadListRef.current;
        if (scrollHeight - scrollTop - clientHeight < 50) {
            setUnreadPage((prev) => prev + 1);
        }
    }, [unreadLoading, unreadHasMore]);

    useEffect(() => {
        if (key === 'all') {
            const ref = listRef.current;
            if (ref) {
                ref.addEventListener('scroll', handleScroll);
                return () => ref.removeEventListener('scroll', handleScroll);
            }
        } else if (key === 'unread') {
            const ref = unreadListRef.current;
            if (ref) {
                ref.addEventListener('scroll', handleUnreadScroll);
                return () => ref.removeEventListener('scroll', handleUnreadScroll);
            }
        }
    }, [handleScroll, handleUnreadScroll, key]);

    // Memoize the formatString function
    const formatString = useCallback((str) => {
        if (str) {
            return str
                .replace(/_/g, ' ')
                .replace(/\b\w/g, c => c.toUpperCase());
        }
        return '';
    }, []);

    // Memoize notification click handler
    const handleNotificationClick = useCallback((notification) => {
        console.log('Clicked notification:', notification);
        dispatch(notificationMarkAsUnread({ notification_id: notification.id }));
        dispatch(fetchNotificationUnreadCount());
        if (notification.type === 'processing_follow_up') {
            setIsProcessingFollowUpView(true);
            setSelectedNotification(notification);
        } else if (notification.type === 'task') {
            setIsTaskViewPopup(true);
            setSelectedNotification(notification);
        } else if (notification.type === 'notification_to_worker_for_not_uploading_photo') {
            window.location.href = `/workers/details/${ notification?.rel_id }?action=photo`;
        } else if (notification.type === 'for_uploading_invoice_payment_attachment_after_24_hours') {
            window.location.href = `/invoices/add-contract-invoice/payment-type-invoice/${ notification?.rel_id }`;
        } else if (notification.type === 'payment_follow_up_to_cs') {
            setIsPaymentFollowUpView(true);
            setSelectedNotification(notification);
        } else if (notification.type === 'change_worker_interpreter') {
            window.location.href = `/workers/details/${ notification?.rel_id }?action=assign-pt`;
        } else if (notification.type === 'assign_invoice_cs' || notification.type === 'change_invoice_cs') {
            window.location.href = `/invoices/add-contract-invoice/payment-type-invoice/${ notification?.rel_id }`;
        } else if (notification.type === 'interview_rescheduled_by_employer_to_interpreter' ||
            notification.type === 'interview_cancelled_by_admin_to_cs' ||
            notification.type === 'interview_rescheduled_by_worker_to_cs' ||
            notification.type === 'interview_cancelled_by_admin_to_interpreter' ||
            notification.type === 'interview_rescheduled_by_employer_to_interpreter' ||
            notification.type === 'interview_cancelled_by_employer_to_cs' ||
            notification.type === 'interview_cancelled_by_employer_to_interpreter' ||
            notification.type === 'worker_no_show' ||
            notification.type === 'interview_cancelled_by_worker' ||
            notification.type === 'upcoming_interview_instant' ||
            notification.type === 'assign_worker_interpreter' ||
            notification.type === 'jo_expired' ||
            notification.type === 'jo_expiring' ||
            notification.type === 'job_matched' ||
            notification.type === 'assign_employer_cs' ||
            notification.type === 'change_employer_cs'
        ) {
            window.location.href = `/employers/details/${ notification?.employer_id }?jobOfferId=${ notification?.job_id }`;
        } else if (notification.type === 'submit_policy_request') {
            window.location.href = `/processings/details/${ notification?.contract_id }?tab=insurance`;
        } else if (notification.type === 'processing_medical_file_uploaded') {
            window.location.href = `/processings/details/${ notification?.contract_id }?tab=medical`;
        } else if (notification.type === 'update_visa_reference_no_notification_to_cs' ||
            notification.type === 'one_month_before_contract_visa_expiry_notification_to_cs'
        ) {
            window.location.href = `/processings/details/${ notification?.contract_id }?tab=visa`;
        } else if (notification.type === 'assign_contract_cs') {
            window.location.href = `/processings/details/${ notification?.contract_id }?tab=contract`;
        }
    }, []);

    // Memoize close handlers
    const handleClose = useCallback(() => {
        if (dropdownToggleRef.current) {
            dropdownToggleRef.current.click();
        }
    }, []);

    const handleOnClose = useCallback(() => {
        setSelectedNotification({});
        setIsProcessingFollowUpView(false);
        setIsPaymentFollowUpView(false);
    }, []);

    const handleOnCloseTaskView = useCallback(() => {
        setSelectedNotification({});
        setIsTaskViewPopup(false);
    }, []);

    // Memoize filtered notifications for unread tab
    // const unreadNotifications = useMemo(() =>
    //     notificationList?.filter(notification => !notification.is_read) || [],
    //     [notificationList]
    // );

    // Memoize notification item rendering
    const renderNotificationItem = useCallback((notification, index) => (
        <li key={ index }
            onClick={ () => handleNotificationClick(notification) }
            className="px-2 py-3 border-bottom mr-0 cursor-pointer list-type-none">
            <div className="d-flex align-items-center">
                <div className={ `${ notification.is_read ? 'read' : '' } circle-icon ml-2 mr-3` }></div>
                <div className="notification-wrapper">
                    <b>{ formatString(notification?.type) }: </b>
                    { notification?.type === 'processing_follow_up' && (
                        <Link
                            to={ `/processings/details/${ notification?.contract_id }` }
                            onClick={ (e) => e.stopPropagation() }
                        >
                            { notification?.contract_number }
                        </Link>
                    ) }
                    { ' ' }
                    { notification?.message }
                </div>
            </div>
            <div className="d-flex align-items-center">
                <div className={ `circle-icon read ml-2 mr-3` }></div>
                <div className="w-100 fz15 fw-400 grey2 mt-2">
                    { dayjs(notification?.created_at).format('ddd D-MMM-YY [at] HH:mm') }
                </div>
            </div>
        </li>
    ), [formatString, handleNotificationClick]);

    const allNotificationsContent = useMemo(() => (
        allNotifications.length > 0 ? (
            <ul className="list-group" ref={ listRef } style={ { maxHeight: '310px', overflowY: 'auto' } }>
                { allNotifications.map((notification, index) =>
                    renderNotificationItem(notification, index)
                ) }
                { loading && (
                    <li className="px-2 py-3 text-center">Loading...</li>
                ) }
            </ul>
        ) : (
            <NotFoundMessage paddingClass="p-3" message={ translation('key_notifications') } />
        )
    ), [allNotifications, renderNotificationItem, loading]);

    const unreadNotificationsContent = useMemo(() => (
        unreadNotificationsList.length > 0 ? (
            <ul className="list-group" ref={ unreadListRef } style={ { maxHeight: '350px', overflowY: 'auto' } }>
                { unreadNotificationsList.map((notification, index) =>
                    renderNotificationItem(notification, index)
                ) }
                { unreadLoading && (
                    <li className="px-2 py-3 text-center">Loading...</li>
                ) }
            </ul>
        ) : (
            <NotFoundMessage paddingClass="p-3" message={ translation('key_notifications') } />
        )
    ), [unreadNotificationsList, renderNotificationItem, unreadLoading]);

    const handleTabSelect = useCallback((k) => {
        setKey(k);
    }, []);

    return (
        <Dropdown className="nav-item notification-dropdown p-0" ref={ dropdownToggleRef }>
            { isProcessingFollowUpView && (
                <ProcessingFollowUpPopupView
                    followUpId={ selectedNotification?.rel_id }
                    onClose={ handleOnClose }
                />
            ) }

            { isPaymentFollowUpView && (<PaymentFollowUpPopupView
                paymentFollowUpId={ selectedNotification?.rel_id }
                onClose={ handleOnClose }
            />) }

            { isTaskViewPopup && (
                <ViewUpdateTaskCalendar
                    taskId={ selectedNotification?.rel_id }
                    onClose={ handleOnCloseTaskView }
                />
            ) }

            <Dropdown.Toggle as="span" className="nav-link chat-notification-wrapper">
                <Link>
                    <img src={ notificationBlue } alt="notification" className="header-notification-icon" />
                    { notificationUnreadCount > 0 && (
                        <span
                            className="notification-badge"
                            style={ {
                                top: '1px',
                                left: '15px'
                            } }
                        >
                            { notificationUnreadCount > 10 ? '10+' : notificationUnreadCount }
                        </span>
                    ) }
                </Link>
            </Dropdown.Toggle>
            <Dropdown.Menu style={ { minWidth: '300px' } }>
                <div className="d-flex align-items-center justify-content-between">
                    <p className="black title mb-0">{ translation('key_notifications') }</p>
                    <Link onClick={ handleClose }>
                        <img src={ crossCircleIcon } alt="close" />
                    </Link>
                </div>
                <Tabs
                    id="notification-tabs"
                    activeKey={ key }
                    onSelect={ handleTabSelect }
                    className="mt-3"
                >
                    <Tab eventKey="all" title={ translation('key_all') } className="fz16">
                        { allNotificationsContent }
                    </Tab>
                    <Tab eventKey="unread" title={ translation('key_unread') } className="fz16">
                        { unreadNotificationsContent }
                    </Tab>
                </Tabs>
                <div className="d-flex justify-content-center">
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
};

NotificationDropdown.propTypes = {
    notificationUnreadCount: PropTypes.number.isRequired,
};
export default React.memo(NotificationDropdown);